import {
  IMicrofrontendData,
  IOttersSharedData,
  ICustomerCompany,
  SharedDataType,
  IAction,
  Tenant,
  ShipperProfile,
  shipperProfileMock,
  GetTokenOptions,
} from '@sennder/senn-node-microfrontend-interfaces'
import { IShipperUserServiceResponse } from '@/src/types/types'
import { getFeatureFlags } from '@sennder/senn-node-feature-flags-frontend'
import { reactive, watch } from 'vue'
import { logger } from '@/services/logger/loggers'
import { logout } from '@/store/logoutActions'
import errorsHandler from '@/services/errors-handler'
export { loadUserData } from './userDataHelper'
import CMDService from '@/services/cmdService'
import { defaultLanguage, i18n } from '@/services/i18n'
import { analytics } from '@/services/analyticsProvider'
import { Optional, Prettify } from '@/types/types'
import { sendErrorInMonitor } from '@/services/monitor'
import shipperUserService from '@/services/shipperUserService'
import startAsyncService from '@/services/retry-service'
import { identifyUserInLaunchDarkly } from '@/services/launchDarkly'
import { getCustomerMockedData } from '@/services/__tests__/cmd-service.mock'
import { auth } from '@/modules/auth'
import { AUTH0_SUS_AUDIENCE } from '@/common/config'
import { OpsCustomerStatus } from '@/types/types'
import { LanguageStorage } from '@/services/LanguageStorage'

declare const shipperUserMock: IShipperUserServiceResponse

const defaultTokenOptions: GetTokenOptions = {
  usePopup: false,
  throwException: false,
}

const getAuthToken = async (
  options = defaultTokenOptions
): Promise<string | null> => {
  const token = await auth.value?.getAuthToken(options)
  return token ?? null
}

const getAuthHeader = async (
  options?: GetTokenOptions
): Promise<string | null> => {
  const token = await getAuthToken(options)
  return token ? `Bearer ${token}` : null
}

async function getCommonHeaders() {
  if (!auth.value) {
    throw new Error(
      '[otters-shell - getCommonHeaders]: Auth module is not loaded'
    )
  }
  return await auth.value.getCommonHeaders()
}

async function getPermissions(actions: IAction[]) {
  if (!auth.value) {
    throw new Error(
      '[otters-shell - getPermissions]: Auth module is not loaded'
    )
  }
  return await auth.value.getPermissions(actions, AUTH0_SUS_AUDIENCE)
}

export type OttersExtendedData = {
  menuCollapsed: boolean
  user: IShipperUserServiceResponse
}

export type OttersStoreData = Prettify<
  Optional<
    IOttersSharedData<OttersExtendedData>,
    'user' | 'company' | 'children' | 'profile'
  >
>

const getEmptyData = (): OttersStoreData => {
  return {
    type: SharedDataType.OTTERS,
    featureFlags: getFeatureFlags() || {},
    language: defaultLanguage,
    tenant: Tenant.SENNDER,
    menuCollapsed: false,
  }
}

const store = reactive({
  state: {
    data: getEmptyData(),
    callbacks: {
      getToken: getAuthHeader,
      getAuthHeader,
      getAuthToken,
      syncParentRouter: () => {},
      segmentTrackEvent: (event, properties) => {
        analytics.trackEvent(event, properties)
      },
      onUnauthorized: async () => {
        return logout()
      },
      onUnhandledError: errorsHandler,
      getCommonHeaders,
      getPermissions,
    },
    providers: {
      logger,
      notifications: {
        error: (message: string, options = {}) =>
          console.log(
            message,
            options,
            '[error notification] function is not implemented.'
          ),
        success: (message: string, options = {}) =>
          console.log(
            message,
            options,
            '[success notification] function is not implemented.'
          ),
      },
      translations: {
        tc: i18n.global.tc,
        t: i18n.global.t,
      },
      segment: analytics,
      analytics,
      monitoring: {
        sendError: sendErrorInMonitor,
      },
    },
  } satisfies IMicrofrontendData<OttersStoreData>,
})

// watch feature flags changes in senn-node-feature-flags-frontend and update store
watch(
  getFeatureFlags,
  (featureFlags) => {
    store.state.data.featureFlags = featureFlags
  },
  { deep: true }
)

export const getStateData = () => {
  return store.state.data
}

export const getStateProfile = () => {
  if (!store.state.data.profile) {
    throw new Error('[Store] state.data.profile is not initialized')
  }
  return store.state.data.profile
}

export const getStateCompany = () => {
  if (!store.state.data.company) {
    throw new Error('[Store] state.data.company is not initialized')
  }
  return store.state.data.company
}

export const getStateProviders = () => {
  return store.state.providers
}

export const getStateCallbacks = () => {
  return store.state.callbacks
}

export const getStateFeatureFlags = () => {
  return store.state.data.featureFlags
}

const visibleCustomers = new Set<string>()
let childCompaniesLoaded = false

export const loadChildCompanies = async () => {
  try {
    if (childCompaniesLoaded || visibleCustomers.size === 0) {
      return
    }

    // in case some of the requests fail we still want to allow users to use ShipperPortal
    // failures are expected because CMD was just released and some bugs are possible
    const results = await Promise.allSettled(
      Array.from(visibleCustomers).map((child) =>
        CMDService.getOpsCustomer(child)
      )
    )

    const cmdResponses = results
      .filter((result) => result.status === 'fulfilled')
      .map((result) => (result as PromiseFulfilledResult<any>).value)

    // log failed responses
    results
      .filter((result) => result.status === 'rejected')
      .forEach((result) => {
        logger.error('[CMD Service]: Failed to load child company', {
          error: (result as PromiseRejectedResult).reason,
        })
      })

    const children = cmdResponses
      // sennder doesn't work with customers in status active, those are not valid customers before they go to status live,
      // other statuses, lost and inactive, are the end of customers' live, which means we have worked with them,
      // there is some data, shipments for example, so the idea is to filter out status active
      // because users should not be able to interact with them
      // probably it'd be better to pass status in the interface and react on it in MFs but it would require a lot more effort
      // and so far this logic was the same everywhere, not only in this shell but also in TMS,
      // so for the sake of time it makes sense to just filter them out here, we can change it to "the interface way" if we ever need it
      .filter((cmdResponse) => cmdResponse.status != OpsCustomerStatus.ACTIVE)
      .map((cmdResponse) => ({
        id: cmdResponse.id,
        companyName: cmdResponse.name,
      }))

    // Mutate state directly because store is reactive
    store.state.data.children = children
  } catch (error) {
    logger.error('[CMD Service]: Child companies cannot be loaded', { error })
  }

  childCompaniesLoaded = true
}

export const loadState = async () => {
  let userInfo: IShipperUserServiceResponse
  let profile: ShipperProfile
  let companyInfo: ICustomerCompany | undefined

  try {
    userInfo = await shipperUserService.getUser()
    const fullName = `${userInfo?.firstName} ${userInfo?.lastName}`
    userInfo.fullNameWithoutTitle = fullName
    userInfo.username = userInfo.email

    if (userInfo.visibleCustomers) {
      userInfo.visibleCustomers.forEach((customer) =>
        visibleCustomers.add(customer)
      )
    }

    profile = {
      email: userInfo.email,
      name: fullName,
      id: userInfo.id,
      language: userInfo.language,
    }
  } catch (error) {
    logger.error('[Shipper User Service]: Data cannot be loaded', { error })
    await logout()
    return false
  }

  try {
    const cmdResponse = await CMDService.getOpsCustomer(userInfo.shipperUuid)
    companyInfo = {
      id: cmdResponse.id,
      companyName: cmdResponse.name,
    }
  } catch (error) {
    logger.error('[SPS Service]: Data cannot be loaded', { error })
    await logout()
    return false
  }

  const menuCollapsedValue = localStorage.getItem('menuCollapsed')
  const menuCollapsed =
    !!menuCollapsedValue && JSON.parse(menuCollapsedValue) === true

  startAsyncService('LaunchDarkly', () => identifyUserInLaunchDarkly(profile))

  store.state.data = {
    ...store.state.data,
    type: SharedDataType.OTTERS,
    user: userInfo,
    company: companyInfo,
    children: [],
    language: defaultLanguage,
    featureFlags: getStateData().featureFlags,
    menuCollapsed,
    tenant: Tenant.SENNDER,
    profile,
  }

  return true
}

export const setStoreLanguage = async (language: string) => {
  store.state.data.language = language
  LanguageStorage.save(language)
}

export const setMenuCollapsed = (collapsed: boolean) => {
  store.state.data.menuCollapsed = collapsed
  localStorage.setItem('menuCollapsed', `${collapsed}`)
}

export const clearState = () => {
  store.state.data = getEmptyData()
}

export const setStoreFeatureFlags = (
  featureFlags: Record<string, boolean | string>
) => {
  store.state.data.featureFlags = featureFlags
}

export const setStoreData = (data: OttersStoreData) => {
  store.state.data = data
}

export const getMicrofrontendData = (isAuthModule = false) => {
  const data = getStateData()

  if (!data.user || !data.profile) {
    if (isAuthModule) {
      data.user = shipperUserMock
      data.profile = shipperProfileMock
    } else {
      throw new Error('state.data.(user | profile) is not initialized')
    }
  }
  if (!data.company) {
    if (isAuthModule) {
      data.company = getCustomerMockedData
    } else {
      throw new Error('state.data.company is not initialized')
    }
  }
  return data as IOttersSharedData<OttersExtendedData>
}
